<script setup lang="ts">
//RowRenderer.vue
import type { Table as VueTable } from '@tanstack/vue-table';
import type { FlatCampaign } from './flattenCampaigns';
import { FlexRender } from '@tanstack/vue-table';
import Button from 'dashboard/components-next/button/Button.vue';
// Usando os componentes shadcn que você baixou
import { TableRow, TableCell } from 'dashboard/components-shadcn/new-york/ui/table';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { campaignStatusLabel } from 'dashboard/routes/dashboard/campaigns/helper/campaignStatus';

interface Props {
  rows: any[];
  depth: number;
  table: VueTable<FlatCampaign>;
  rowGrouping: string[];
  isExpanding?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isExpanding: false,
});

defineOptions({
  name: 'RowRenderer',
});

const { t } = useI18n();

// ✅ Mapeamento de tradução das colunas (mesmo do DataTableFacetedGroup)
const columnTranslations: Record<string, string> = {
  'campaign_status': 'Status da Campanha',
  'sender_name': 'Usuário',
  'status': 'Status',
  'title': 'Título',
  'user': 'Usuário',
  'inbox_name': 'Caixa de Entrada',
  'scheduled_at': 'Agendado para',
  'created_at': 'Criado em',
  'updated_at': 'Atualizado em',
  'priority': 'Prioridade',
  'category': 'Categoria',
  'type': 'Tipo',
  'channel': 'Canal',
  'department': 'Departamento',
  'team': 'Equipe',
  'assignee': 'Responsável',
  'contact': 'Contato',
  'tags': 'Tags',
  'source': 'Origem',
  'campaign_type': 'Tipo de Campanha',
  'delivery_status': 'Status de Entrega'
};

// ✅ Função para obter o nome traduzido da coluna
const getTranslatedColumnName = (columnId: string): string => {
  return columnTranslations[columnId] || columnId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Computed para otimizar cálculos de indentação
const indentStyle = computed(() => ({
  paddingLeft: `${props.depth * 1.5}rem`,
}));

// Computed para melhor formatação do valor do grupo
const formatGroupValue = (row: any, columnKey: string) => {
  const value = row.getValue(columnKey);

  if (value === null || value === undefined) {
    return 'Sem valor';
  }

  if (typeof value === 'string' && value.trim() === '') {
    return 'Vazio';
  }

  // Agrupamento por status: o valor é o enum CRU → traduz pra exibição
  if (columnKey === 'campaign_status') {
    return campaignStatusLabel(value, t);
  }

  // Formatação personalizada para diferentes tipos
  if (typeof value === 'number') {
    return value.toLocaleString();
  }

  if (value instanceof Date) {
    return value.toLocaleDateString();
  }

  return String(value);
};

// Computed para estatísticas do grupo
const getGroupStats = (row: any) => {
  if (!row.getIsGrouped()) return null;

  const leafRows = row.getLeafRows();
  const selectedLeafRows = leafRows.filter((leaf: any) => leaf.getIsSelected());

  return {
    total: leafRows.length,
    selected: selectedLeafRows.length,
  };
};

// ✅ Função para determinar ícone baseado no tipo de coluna (atualizada com novos campos)
const getGroupIcon = (columnKey: string) => {
  const iconMap: Record<string, string> = {
    'status': '📊',
    'campaign_status': '📢',
    'inbox_name': '📧',
    'title': '📝',
    'user': '👤',
    'date': '📅',
    'priority': '⭐',
    'category': '🏷️',
    'created_at': '📅',
    'updated_at': '🔄',
    'scheduled_at': '⏰',
    'type': '🔖',
    'channel': '📺',
    'department': '🏢',
    'team': '👥',
    'assignee': '👤',
    'contact': '📞',
    'tags': '🏷️',
    'source': '🌍',
    'campaign_type': '📋',
    'delivery_status': '📬'
  };

  return iconMap[columnKey] || '📁';
};

// Computed para ícone dinâmico do botão
const getExpandIcon = (row: any) => {
  return row.getIsExpanded() ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right';
};

// Computed para tooltip dinâmico
const getExpandTooltip = (row: any) => {
  return row.getIsExpanded() ? 'Colapsar grupo' : 'Expandir grupo';
};
</script>

<template>
  <template v-for="row in rows" :key="row.id">
    <!-- Linha de Grupo -->
    <TableRow
      v-if="row.getIsGrouped()"
      :data-state="row.getIsSelected() ? 'selected' : undefined"
      class="group-row bg-green-50/50 dark:bg-green-800/30 hover:bg-green-100/50 dark:hover:bg-slate-700/50 transition-colors border-l-3 border-green-600 dark:border-green-400"
      :class="{ 'expanding': isExpanding }"
    >
      <TableCell
        :colspan="table.getAllColumns().length"
        :style="indentStyle"
        class="font-medium border-b border-slate-200 dark:border-slate-800"
      >
        <div class="flex items-center gap-2 py-1">
          <!-- Botão de Expansão usando o componente Button -->
          <Button
            variant="faded"
            color="slate"
            size="xs"
            :icon="getExpandIcon(row)"
            @click="row.toggleExpanded()"
            :disabled="isExpanding"
            class="ml-2"
          />

          <!-- Ícone do Grupo -->
          <span class="text-lg">
            {{ getGroupIcon(rowGrouping[depth]) }}
          </span>

          <!-- Informações do Grupo -->
          <div class="flex items-center gap-2">
            <span class="text-sm font-semibold capitalize text-green-700 dark:text-n-slate-11">
              {{ getTranslatedColumnName(rowGrouping[depth]) || 'Grupo' }}:
            </span>
            <span class="text-sm text-n-slate-12 dark:text-n-slate-11">
              {{ formatGroupValue(row, rowGrouping[depth]) }}
            </span>
          </div>

          <!-- Estatísticas do Grupo -->
          <div class="ml-auto flex items-center gap-2 text-xs text-n-slate-9 dark:text-n-slate-8">
            <div class="flex items-center gap-1">
              <!-- Substituído UsersIcon por ícone Lucide CSS -->
              <span class="i-lucide-megaphone w-4 h-4"></span>
              <span>{{ getGroupStats(row)?.total || 0 }}</span>
            </div>

            <div
              v-if="getGroupStats(row)?.selected > 0"
              class="flex items-center gap-1 text-green-600 dark:text-green-400"
            >
              <span>•</span>
              <span>{{ getGroupStats(row)?.selected }} selecionados</span>
            </div>

            <!-- Indicador de Carregamento -->
            <div v-if="isExpanding" class="flex items-center gap-1">
              <div class="w-3 h-3 border border-green-500 border-t-transparent rounded-full animate-spin"></div>
              <span class="text-xs">Processando...</span>
            </div>
          </div>
        </div>
      </TableCell>
    </TableRow>

    <!-- Linha de Dados -->
    <TableRow
      v-else
      :data-state="row.getIsSelected() ? 'selected' : undefined"
      class="leaf-row hover:bg-green-50/50 dark:hover:bg-green-800/20 transition-colors"
      :class="{
        'expanding': isExpanding,
        [`depth-${depth}`]: depth > 0,
        'bg-green-50 dark:bg-green-900/20': row.getIsSelected()
      }"
    >
      <TableCell
        v-for="cell in row.getVisibleCells()"
        :key="cell.id"
        :style="depth > 0 ? indentStyle : undefined"
        class="relative border-b border-slate-200 dark:border-slate-800"
      >
        <!-- Indicador visual de hierarquia -->
        <div
          v-if="depth > 0"
          class="absolute w-[0.5px] bg-slate-300 dark:bg-slate-600"

        ></div>

        <!-- Conteúdo da Célula -->
        <div class="flex items-center gap-2">
          <!-- Indicador de nível para a primeira coluna -->
          <div
            v-if="cell.column.getIndex() === 0 && depth > 0"
            class="flex items-center text-slate-500 dark:text-slate-400 mr-2"
          >
            <div class="flex items-center">
              <div class="w-3 h-px bg-slate-400 dark:bg-slate-600"></div>
              <div class="w-px h-3 bg-slate-400 dark:bg-slate-600 -ml-px"></div>
            </div>
          </div>

          <!-- Renderização do conteúdo -->
          <FlexRender
            v-if="cell.column.columnDef.cell"
            :render="cell.column.columnDef.cell"
            :props="cell.getContext()"
          />
          <span v-else class="text-sm text-n-slate-12 dark:text-n-slate-11">
            {{ cell.getValue() || 'N/A' }}
          </span>
        </div>
      </TableCell>
    </TableRow>

    <!-- Renderização Recursiva das Sub-linhas -->
    <template v-if="row.getIsGrouped() && row.getIsExpanded() && row.subRows?.length">
      <Transition
        name="expand"
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <RowRenderer
          :rows="row.subRows"
          :depth="depth + 1"
          :table="table"
          :row-grouping="rowGrouping"
          :is-expanding="isExpanding"
        />
      </Transition>
    </template>
  </template>
</template>

<style scoped>
/* Estilos para diferentes níveis de profundidade usando sua paleta customizada */
.depth-1 { 
  background: rgba(var(--green-50), 0.5);
}

.dark .depth-1 { 
  background: rgba(var(--green-900), 0.1);
}

.depth-2 { 
  background: rgba(var(--green-100), 0.5);
}

.dark .depth-2 { 
  background: rgba(var(--green-900), 0.2);
}

.depth-3 { 
  background: rgba(var(--green-200), 0.5);
}

.dark .depth-3 { 
  background: rgba(var(--green-900), 0.3);
}

/* Estilos para linhas de grupo usando cores da sua paleta */
.group-row {
  border-left: 3px solid rgb(var(--green-600));
}

.dark .group-row {
  border-left: 3px solid rgb(var(--green-400));
}

/* Estados de carregamento */
.expanding {
  opacity: 0.7;
  pointer-events: none;
}

/* Animações suaves */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.2s ease;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Hover effects com sua paleta */
.group-row:hover {
  background: rgba(var(--green-100), 0.5);
  box-shadow: 0 2px 4px rgba(var(--green-500), 0.1);
}

.dark .group-row:hover {
  background: rgba(var(--slate-700), 0.5);
  box-shadow: 0 2px 4px rgba(var(--green-500), 0.2);
}

.leaf-row:hover {
  background: rgba(var(--green-50), 0.5);
  box-shadow: 0 1px 2px rgba(var(--green-500), 0.05);
}

.dark .leaf-row:hover {
  background: rgba(var(--green-800), 0.2);
  box-shadow: 0 1px 2px rgba(var(--green-500), 0.1);
}

/* Indicadores visuais de hierarquia */
.hierarchy-indicator {
  position: relative;
}

.hierarchy-indicator::before {
  content: '';
  position: absolute;
  left: -1rem;
  top: 50%;
  width: 0.75rem;
  height: 1px;
  background: rgb(var(--slate-300));
}

.dark .hierarchy-indicator::before {
  background: rgb(var(--slate-600));
}

/* Garantir que todas as células sigam o padrão do componente pai */
:deep(.whatsapp-table tbody tr td) {
  border-bottom: 1px solid rgb(var(--slate-200));
}

.dark :deep(.whatsapp-table tbody tr td) {
  border-bottom: 1px solid rgb(var(--slate-800));
}
:deep(tr[data-state="selected"]) {
  background: rgba(var(--green-50), 1);
}

.dark :deep(tr[data-state="selected"]) {
  background: rgba(var(--green-900), 0.2);
}

:deep(tr[data-state="selected"]:hover) {
  background: rgba(var(--green-100), 1);
}

.dark :deep(tr[data-state="selected"]:hover) {
  background: rgba(var(--green-900), 0.3);
}

/* Customização de cores primárias */
:deep(.text-primary) {
  color: rgb(var(--green-600));
}

.dark :deep(.text-primary) {
  color: rgb(var(--green-400));
}

:deep(.border-primary) {
  border-color: rgb(var(--green-500));
}

:deep(.bg-primary) {
  background: rgb(var(--green-500));
}

/* Estados de muted */
:deep(.bg-muted) {
  background: rgba(var(--green-100), 1);
}

.dark :deep(.bg-muted) {
  background: rgba(var(--green-800), 1);
}

:deep(.text-muted-foreground) {
  color: rgb(var(--slate-500));
}

.dark :deep(.text-muted-foreground) {
  color: rgb(var(--slate-400));
}

:deep(.bg-background) {
  background: rgb(var(--background-color));
}

:deep(.bg-border) {
  background: rgba(var(--green-200), 1);
}

.dark :deep(.bg-border) {
  background: rgba(var(--green-700), 1);
}

/* Responsividade */
@media (max-width: 768px) {
  .group-row .flex {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .group-row .ml-auto {
    margin-left: 0;
    width: 100%;
    justify-content: flex-start;
  }

  .leaf-row .relative {
    padding: 0.5rem;
  }
}

/* Estados de foco */
button:focus-visible {
  outline: 2px solid rgb(var(--green-500));
  outline-offset: 2px;
}

/* Transições suaves */
button {
  transition: all 0.2s ease;
}

.group-row,
.leaf-row {
  transition: all 0.2s ease;
}
</style>